

export class Setting<T> {
    private apiDelegate: () => Promise<T>;
    private cacheKey: string | (() => string);
    private cacheDuration: number;
    private isValidCachedValue?: (value: T) => boolean;
    USE_ENCODE = true;

    constructor(cacheKey: string | (() => string), cacheDurationMinutes: number, apiDelegate: () => Promise<T>, isValidCachedValue?: (value: T) => boolean) {
        this.apiDelegate = apiDelegate;
        this.cacheKey = cacheKey;
        this.cacheDuration = cacheDurationMinutes * 60 * 1000; // Convert minutes to milliseconds
        this.isValidCachedValue = isValidCachedValue;
    }

    private getCacheKey(): string {
        return typeof this.cacheKey === 'function' ? this.cacheKey() : this.cacheKey;
    }

    private getCacheExpiryKey(): string {
        return `${this.getCacheKey()}_exp`;
    }

    private encode(value: T): string {
        return this.USE_ENCODE ? btoa(JSON.stringify(value)): JSON.stringify(value);
    }

    private decode(encodedValue: string): T {
        return this.USE_ENCODE ? JSON.parse(atob(encodedValue)) : JSON.parse(encodedValue);
    }

    async value(): Promise<T> {
        const now = new Date().getTime();
        const cacheKey = this.getCacheKey();
        const cacheExpiryKey = this.getCacheExpiryKey();
        const cachedValue = localStorage.getItem(cacheKey);
        const cacheExpiry = localStorage.getItem(cacheExpiryKey);

        try {
            if (cachedValue && cacheExpiry && now < parseInt(cacheExpiry, 10)) {
                const decoded = this.decode(cachedValue) as T;
                if (!this.isValidCachedValue || this.isValidCachedValue(decoded)) {
                    return decoded;
                }
            }
        }
        catch (error) {
            // re-fetch
        }

        const value = await this.apiDelegate();

        if (value != null) {
            localStorage.setItem(cacheKey, this.encode(value));
            localStorage.setItem(cacheExpiryKey, (now + this.cacheDuration).toString());    
        }

        return value;
    }

    clear(): void {
        localStorage.removeItem(this.getCacheKey());
        localStorage.removeItem(this.getCacheExpiryKey());
    }
}
